<script setup lang="ts">
import { formatMarketTokenPrice } from '../../trading'
import { computed } from 'vue'
import { clamp } from '@shared/utils'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { BAND_INSIDE_LABEL, BAND_OUTSIDE_LABEL } from '../constants/sellCopy'

// Permitted-band track (Figma "Band", 34842:194788 / 34842:194873): band ends
// and the valuation above a grey track with a valuation tick and a knob at
// your price. Inside the band the whole track tints green; outside, a red fill
// runs up to the knob, which pins to the nearer end.

interface Props {
  bandMin: number
  bandMax: number
  valuation: number
  /** Typed price; null before a valid number is entered. */
  price: number | null
  isOutside: boolean
}

const props = defineProps<Props>()

const PERCENT = 100

const positionOf = (value: number) => {
  const span = props.bandMax - props.bandMin
  return span > 0 ? clamp(((value - props.bandMin) / span) * PERCENT, 0, PERCENT) : 0
}

const valuationPct = computed(() => positionOf(props.valuation))
const pricePct = computed(() => (props.price === null ? null : positionOf(props.price)))
</script>

<template>
  <Box direction="col" gap="2" :class="['sell-band', { 'sell-band--outside': isOutside }]">
    <Box align="start" justify="between" gap="2">
      <Text is="span" type="caption" color="var(--p-surface-500)" class="sell-band__label"
        ><CurrencyText :text="formatMarketTokenPrice(bandMin)"
      /></Text>
      <Text is="span" type="caption" weight="medium" color="default" class="sell-band__label"
        >Valuation <CurrencyText :text="formatMarketTokenPrice(valuation)"
      /></Text>
      <Text is="span" type="caption" color="var(--p-surface-500)" class="sell-band__label"
        ><CurrencyText :text="formatMarketTokenPrice(bandMax)"
      /></Text>
    </Box>

    <Box class="sell-band__track" aria-hidden="true">
      <Box
        class="sell-band__fill"
        :style="{ width: isOutside && pricePct !== null ? `${pricePct}%` : '100%' }"
      />
      <Box class="sell-band__tick" :style="{ left: `${valuationPct}%` }" />
      <Box v-if="pricePct !== null" class="sell-band__knob" :style="{ left: `${pricePct}%` }" />
    </Box>

    <Text
      is="span"
      type="caption"
      :color="isOutside ? 'var(--p-danger-600)' : 'var(--p-surface-500)'"
      class="sell-band__status"
      >{{ isOutside ? BAND_OUTSIDE_LABEL : BAND_INSIDE_LABEL }}</Text
    >
  </Box>
</template>

<style scoped>
.sell-band {
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--p-sliceal-gray-100);
  box-sizing: border-box;
}

.sell-band__label {
  line-height: 1.3;
  white-space: nowrap;
}

.sell-band__track {
  position: relative;
  height: 8px;
  margin: 4px 8px;
  border-radius: 40px;
  background: var(--p-sliceal-gray-200);
}

.sell-band__fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 8px;
  border-radius: 40px;
  background: var(--p-success-100);
}

.sell-band--outside .sell-band__fill {
  background: var(--p-danger-100);
}

.sell-band__tick {
  position: absolute;
  top: 0;
  width: 2px;
  height: 8px;
  transform: translateX(-50%);
  background: var(--p-sliceal-gray-400);
}

.sell-band__knob {
  position: absolute;
  top: 50%;
  width: 16px;
  height: 16px;
  border: 3px solid var(--p-success-600);
  border-radius: 50%;
  background: var(--p-surface-0);
  box-sizing: border-box;
  transform: translate(-50%, -50%);
  transition: left 0.15s ease;
}

.sell-band__fill {
  transition: width 0.15s ease;
}

.sell-band--outside .sell-band__knob {
  border-color: var(--p-danger-600);
}

.sell-band__status {
  line-height: 1.4;
}

@media (prefers-reduced-motion: reduce) {
  .sell-band__knob,
  .sell-band__fill {
    transition: none;
  }
}
</style>
